/**
 * Block 13 PR-G Phase 5A + 6.5G A8 + 6.5G hotfix (2026-05-25) — Container
 * Экрана 2.13.1 «Договор» (ADR-0005).
 *
 * Orchestrates:
 *   - Mount-time fetch through `listSavedContracts()` (full list — no real
 *     pagination, см. B-156).
 *   - Sort by `updatedAt DESC` — newest first per UX expectation.
 *   - 4-state machine (loading → success/empty/error) maps к ContractListView
 *     P-021 states.
 *   - Retry callback regenerates fetch.
 *
 * **Phase 6.5G A8 (preserved):**
 *   - Local state `isCreateSheetOpen` / `isQuotesSheetOpen` для chained sheets.
 *   - «Создать договор» button → opens ChoiceSheetCreateMode → user выбирает
 *     режим:
 *       - «Заполнить вручную» → navigate `/contracts/new?manual=true`.
 *       - «Взять данные из КП» → chain в ChoiceSheetForQuotes → on select →
 *         navigate `/contracts/new?fromQuote=<uuid>`.
 *
 * **Phase 6.5G hotfix changes (Aibek visual verify round 6 fix #3):**
 *   - **Удалены** `handleOpenClick` callback + `isEmpty` derived prop —
 *     «Открыть договор» button заменена на conditional text-указатель в
 *     ContractListView. Anchor `#contracts-list-region` сохраняется (cosmetic).
 *
 * Reference:
 *   - ADR-0025 Amendment A8.7
 *   - PRD §10.13.1
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ChoiceSheetCreateMode } from './components/ChoiceSheetCreateMode';
import { ChoiceSheetForQuotes } from './components/ChoiceSheetForQuotes';
import { ContractListView, type ContractListStatus } from './ContractListView';
import {
  deleteSavedContract,
  listSavedContracts,
  type SavedContract,
} from './state/savedContractsIdb';
import type { SavedQuote } from '@/features/calculator/quote/state/savedQuotesIdb';

type LoadState =
  | { status: 'loading' }
  | { status: 'success'; contracts: SavedContract[] }
  | { status: 'empty' }
  | { status: 'error' };

export function ContractListContainer() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  // Phase 6.5G A8 — chained sheet state (Create choice → optional Quotes
  // choice → navigate).
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isQuotesSheetOpen, setIsQuotesSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    listSavedContracts()
      .then((items) => {
        if (cancelled) return;
        if (items.length === 0) {
          setState({ status: 'empty' });
          return;
        }
        const sorted = [...items].sort((a, b) => b.updatedAt - a.updatedAt);
        setState({ status: 'success', contracts: sorted });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (import.meta.env.DEV) {
          const errName = err instanceof Error ? err.name : 'unknown';
          console.error('[ContractListContainer] listSavedContracts failed', errName);
        }
        setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const viewStatus: ContractListStatus = state.status;
  const contracts = state.status === 'success' ? state.contracts : undefined;

  const handleCreateClick = useCallback(() => setIsCreateSheetOpen(true), []);

  const handleSelectManual = useCallback(() => {
    navigate('/contracts/new?manual=true');
  }, [navigate]);

  const handleSelectFromQuote = useCallback(() => {
    // Chained sheet — Create choice closes (через onClose), затем opens Quotes.
    setIsQuotesSheetOpen(true);
  }, []);

  const handleQuoteSelected = useCallback(
    (quote: SavedQuote) => {
      navigate(`/contracts/new?fromQuote=${quote.id}`);
    },
    [navigate],
  );

  // Phase 7 hotfix round 7 (R7-#2, 2026-05-26): delete контракта flow. После
  // confirm в DeleteContractDialog (rendered внутри ContractCard) →
  // deleteSavedContract(id) → refetch list через attempt bump.
  const handleDelete = useCallback(async (id: string): Promise<void> => {
    try {
      await deleteSavedContract(id);
      setAttempt((n) => n + 1);
    } catch (err) {
      if (import.meta.env.DEV) {
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[ContractListContainer] deleteSavedContract failed', errName);
      }
      // P-021 error state — refetch чтобы list state синхронизировался с IDB.
      setAttempt((n) => n + 1);
    }
  }, []);

  return (
    <>
      <ContractListView
        status={viewStatus}
        contracts={contracts}
        onRetry={() => setAttempt((n) => n + 1)}
        onCreateClick={handleCreateClick}
        onDelete={handleDelete}
      />
      <ChoiceSheetCreateMode
        isOpen={isCreateSheetOpen}
        onClose={() => setIsCreateSheetOpen(false)}
        onSelectFromQuote={handleSelectFromQuote}
        onSelectManual={handleSelectManual}
        title="Создание договора"
      />
      <ChoiceSheetForQuotes
        isOpen={isQuotesSheetOpen}
        onClose={() => setIsQuotesSheetOpen(false)}
        onSelect={handleQuoteSelected}
        title="Выберите КП"
        subtitle="Заполненные позиции из КП попадут в новый договор"
      />
    </>
  );
}
